/**
 * Google Analytics 4 — centralized event helpers.
 *
 * The GA4 Measurement ID is read from the environment variable
 * REACT_APP_GA4_MEASUREMENT_ID.  The gtag snippet itself is loaded
 * in public/index.html so it is available before React boots.
 *
 * Usage:
 *   import { trackResumeDownload } from "@/lib/analytics";
 *   <a onClick={trackResumeDownload} …>
 */

// ---------------------------------------------------------------------------
// Low-level gtag wrapper
// ---------------------------------------------------------------------------

/**
 * Safely call `window.gtag`.  No-ops when GA is not loaded (e.g. local dev
 * without a Measurement ID, or if the user has an ad-blocker).
 */
export function gtag(...args) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag(...args);
  }
}

/**
 * Fire a GA4 custom event.
 * @param {string} eventName  — snake_case GA4 event name
 * @param {Record<string, string|number>} [params] — optional event parameters
 */
export function trackEvent(eventName, params = {}) {
  gtag("event", eventName, params);
}

// ---------------------------------------------------------------------------
// Named event helpers — keep event names consistent across the codebase
// ---------------------------------------------------------------------------

/** Resume download (hero button, resume section, navbar, contact channel) */
export function trackResumeDownload(location = "unknown") {
  trackEvent("resume_download", { event_location: location });
}

/** Resume preview (resume section "Preview" button) */
export function trackResumePreview() {
  trackEvent("resume_preview", { event_location: "resume_section" });
}

/** GitHub profile click */
export function trackGitHubClick(location = "unknown") {
  trackEvent("github_click", { event_location: location });
}

/** LinkedIn profile click */
export function trackLinkedInClick(location = "unknown") {
  trackEvent("linkedin_click", { event_location: location });
}

/** Email / mailto click */
export function trackEmailClick(location = "unknown") {
  trackEvent("email_click", { event_location: location });
}

/** Portfolio project card click — "Live Demo" */
export function trackProjectLiveDemo(projectName) {
  trackEvent("project_live_demo", { project_name: projectName });
}

/** Portfolio project card click — "View Source / GitHub" */
export function trackProjectViewSource(projectName) {
  trackEvent("project_view_source", { project_name: projectName });
}

/** Contact form submitted successfully */
export function trackContactFormSubmit() {
  trackEvent("contact_form_submit");
}

/** Navbar resume CTA click */
export function trackNavResumeClick() {
  trackEvent("nav_resume_click", { event_location: "navbar" });
}

/** Footer social link click */
export function trackFooterSocialClick(platform) {
  trackEvent("footer_social_click", { platform });
}

/**
 * Manual page-view for SPA route changes.
 * GA4's auto-config only fires on the initial full-page load.
 * Call this on every React Router navigation to get accurate page views.
 *
 * @param {string} path  — e.g. "/" or "/blog/my-post"
 * @param {string} [title] — document title at the time of navigation
 */
export function trackPageView(path, title) {
  gtag("config", process.env.REACT_APP_GA4_MEASUREMENT_ID, {
    page_path: path,
    page_title: title || document.title,
  });
}
